/**
 * Slice F3 — explicit Clerk bridge recovery states.
 *
 * This module is the SINGLE source of truth mapping a bridge failure's
 * `error_type` to a stable, testable recovery outcome. `ClerkBridgeCoordinator`
 * previously collapsed every failure into one generic "could not be completed"
 * alert, which made distinct, differently-actionable problems (an expired token,
 * an account collision, a rate limit, a real outage) indistinguishable to both
 * users and testers.
 *
 * Rules that hold for EVERY row:
 * - Never expose token contents, Clerk subjects, emails, or vendor payloads.
 * - Never offer legacy credentials or local registration as a recovery path.
 *   During BETA/ACTIVE the backend fails legacy closed, so such an offer is a
 *   dead end that also contradicts the Clerk cutover.
 * - Never perform browser-side account linking or invent a backend mutation.
 * - Never auto-retry on a loop. `canRetry` renders an explicit user-driven button.
 *
 * COPY IS PROVISIONAL. Owner-approved wording and the real support destination
 * are still required inputs (see the F3 section of the implementation plan).
 * `SUPPORT_DESTINATION` is intentionally null until that decision lands, and the
 * UI degrades to describing the action without inventing a contact route.
 */

// Owner decision pending: the real support contact/route for collision and
// migration recovery. Do NOT invent an address here.
export const SUPPORT_DESTINATION = null;

/**
 * How the user can act on a recovery state.
 * - `retry`      — re-attempt the bridge with the existing Clerk session.
 * - `signOut`    — end the session and return to a clean sign-in.
 * - `support`    — the user cannot self-serve; they must contact support.
 * - `none`       — terminal wait state; nothing useful for the user to press.
 */
export const RECOVERY_ACTIONS = {
    RETRY: 'retry',
    SIGN_OUT: 'signOut',
    SUPPORT: 'support',
    NONE: 'none'
};

const RECOVERY_STATES = {
    // 401 — the fresh-token retry in bridgeWithTokenRetry already ran and failed.
    token_invalid: {
        title: 'Your sign-in session expired',
        message: 'For your security, please sign in again to continue.',
        action: RECOVERY_ACTIONS.SIGN_OUT,
        actionLabel: 'Sign in again'
    },
    // 401 — Clerk reported a pending session task the SDK should own. If the SDK
    // is not showing one, stop here rather than looping back into the bridge.
    session_task_required: {
        title: 'One more step is needed',
        message: 'Your sign-in needs to be completed before you can continue.',
        action: RECOVERY_ACTIONS.SIGN_OUT,
        actionLabel: 'Restart sign-in'
    },
    // 403 — rollout policy denies Clerk for this user/state. Never offer local
    // registration as a workaround.
    clerk_auth_unavailable: {
        title: 'Sign-in is not available for this account yet',
        message: 'Your account can\u2019t use this sign-in method right now.',
        action: RECOVERY_ACTIONS.SIGN_OUT,
        actionLabel: 'Sign out'
    },
    // 409 — Clerk profile is missing data provisioning requires.
    clerk_profile_incomplete: {
        title: 'Your profile needs more information',
        message: 'Some details are missing from your account and we can\u2019t finish setting it up.',
        action: RECOVERY_ACTIONS.SUPPORT,
        actionLabel: 'Contact support'
    },
    // 409 — a Django user already holds this email. NEVER create another user and
    // never attempt browser-side linking; resolution is a support/ops decision.
    email_already_registered: {
        title: 'An account already uses this email',
        message: 'We found an existing account with this email address. To avoid creating a duplicate, our team needs to connect them for you.',
        action: RECOVERY_ACTIONS.SUPPORT,
        actionLabel: 'Contact support'
    },
    // 409 — deliberately distinct from the email collision: different cause,
    // different resolution, and conflating them would misdirect the user.
    username_taken: {
        title: 'That display name is already taken',
        message: 'The name on your account is already in use. Our team can help you choose a different one.',
        action: RECOVERY_ACTIONS.SUPPORT,
        actionLabel: 'Contact support'
    },
    // 409 — this identity is mid-migration. Never offer registration, which would
    // create a duplicate of an account that already exists.
    migration_incomplete: {
        title: 'Your account is still being moved',
        message: 'We\u2019re still finishing the move of your existing account. Please try again shortly, or contact us if this continues.',
        action: RECOVERY_ACTIONS.SUPPORT,
        actionLabel: 'Contact support'
    },
    // 429 — explicit retry only. An automatic retry here would deepen the
    // throttle the user is already hitting.
    throttled: {
        title: 'Too many attempts',
        message: 'You\u2019ve tried to sign in several times in a row. Please wait a moment and try again.',
        action: RECOVERY_ACTIONS.RETRY,
        actionLabel: 'Try again'
    },
    // 503 — Clerk or the identity service is down. Retryable, never a legacy fallback.
    clerk_unavailable: {
        title: 'Sign-in is temporarily unavailable',
        message: 'We can\u2019t reach the sign-in service right now. Your account is safe \u2014 please try again in a few moments.',
        action: RECOVERY_ACTIONS.RETRY,
        actionLabel: 'Try again'
    },
    clerk_identity_unavailable: {
        title: 'Sign-in is temporarily unavailable',
        message: 'We can\u2019t reach the sign-in service right now. Your account is safe \u2014 please try again in a few moments.',
        action: RECOVERY_ACTIONS.RETRY,
        actionLabel: 'Try again'
    },
    // Transport-level failure: non-JSON body, gateway error, or a malformed
    // success payload. Retryable and never silently swallowed.
    invalid_response: {
        title: 'Something went wrong',
        message: 'We hit an unexpected problem completing your sign-in. Please try again.',
        action: RECOVERY_ACTIONS.RETRY,
        actionLabel: 'Try again'
    }
};

/**
 * Error types that are NOT recovery states because they are handled elsewhere in
 * the coordinator. Listing them explicitly keeps `resolveRecoveryState` honest:
 * they are known codes with owners, not unmapped gaps.
 *
 * - `beta_key_required` / `beta_key_invalid` are Slice F2's admission flow.
 * - `beta_admission_token_invalid` is the SAME admission flow, one layer out: the
 *   short-lived carrier minted by the beta-key precheck was unknown, expired, or
 *   already redeemed. It is emphatically NOT a recovery screen — the user's key
 *   itself may be perfectly good, and the correct response is to discard the dead
 *   token and show the ordinary admission prompt so they can enter that key.
 *   Routing it to recovery would tell someone with a valid invite that sign-in
 *   failed, which is both wrong and a dead end.
 *
 *   Note the deliberate contrast with `beta_key_invalid`, which the backend also
 *   returns when a VALID token's underlying key was exhausted or revoked
 *   mid-redirect. There, retrying with the same key is pointless; here, the key
 *   was never the problem.
 */
export const NON_RECOVERY_ERROR_TYPES = new Set([
    'beta_key_required',
    'beta_key_invalid',
    'beta_admission_token_invalid'
]);

/**
 * Resolve a bridge error into its recovery state.
 *
 * Returns `null` for error types owned by another flow (beta admission), so the
 * caller can assert it never renders a recovery screen for those.
 *
 * Throws for an unrecognized or missing `error_type`. This is deliberate: a new
 * backend error code must not silently degrade into a vague generic alert, which
 * is exactly the F3 defect being fixed. A loud failure surfaces the unmapped code
 * during development instead of shipping a dead-end screen.
 */
export function resolveRecoveryState(error) {
    const errorType = error?.errorType;
    if (typeof errorType !== 'string' || !errorType) {
        throw new Error(
            'Clerk bridge error is missing an errorType; the API layer must always '
            + `classify failures. Received: ${JSON.stringify(errorType)}`
        );
    }
    if (NON_RECOVERY_ERROR_TYPES.has(errorType)) return null;

    const state = RECOVERY_STATES[errorType];
    if (!state) {
        throw new Error(
            `Unmapped Clerk bridge error_type: "${errorType}". Add an explicit recovery `
            + 'state for it rather than falling back to a generic message.'
        );
    }
    return { errorType, ...state, supportDestination: SUPPORT_DESTINATION };
}

/** Every mapped error type, for exhaustiveness testing. */
export function mappedRecoveryErrorTypes() {
    return Object.keys(RECOVERY_STATES);
}
